export type MoneyBuckets = {
  invoice: number;
  cash: number;
};

export function toCents(value: string | number | null | undefined): number {
  const amount = typeof value === 'number' ? value : Number(value ?? 0);
  if (Number.isNaN(amount)) return 0;
  return Math.round(amount * 100);
}

export function fromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function bucketsFromTotals(totals: {
  invoice: string;
  cash: string;
}): MoneyBuckets {
  return {
    invoice: toCents(totals.invoice),
    cash: toCents(totals.cash),
  };
}

export function bucketsTotal(buckets: MoneyBuckets): number {
  return buckets.invoice + buckets.cash;
}

export function addBuckets(a: MoneyBuckets, b: MoneyBuckets): MoneyBuckets {
  return {
    invoice: a.invoice + b.invoice,
    cash: a.cash + b.cash,
  };
}

/**
 * Same-type first, then leftover of one type covers remaining of the other.
 * Mirrors backend `consumeSameThenCross`.
 */
export function consumeSameThenCross(
  pay: MoneyBuckets,
  invoice: MoneyBuckets,
): { take: MoneyBuckets; nextPay: MoneyBuckets; nextInvoice: MoneyBuckets } {
  const payInvoice = Math.max(pay.invoice, 0);
  const payCash = Math.max(pay.cash, 0);
  const invInvoice = Math.max(invoice.invoice, 0);
  const invCash = Math.max(invoice.cash, 0);

  const sameInvoice = Math.min(payInvoice, invInvoice);
  const sameCash = Math.min(payCash, invCash);
  let remainingInvoice = invInvoice - sameInvoice;
  let remainingCash = invCash - sameCash;
  const leftoverPayInvoice = payInvoice - sameInvoice;
  const leftoverPayCash = payCash - sameCash;

  const crossCash = Math.min(leftoverPayInvoice, remainingCash);
  remainingCash -= crossCash;
  const crossInvoice = Math.min(leftoverPayCash, remainingInvoice);
  remainingInvoice -= crossInvoice;

  const take = {
    invoice: sameInvoice + crossInvoice,
    cash: sameCash + crossCash,
  };

  return {
    take,
    nextPay: {
      invoice: pay.invoice - take.invoice,
      cash: pay.cash - take.cash,
    },
    nextInvoice: {
      invoice: remainingInvoice,
      cash: remainingCash,
    },
  };
}

export function pairKey(paymentId: number, invoiceId: number) {
  return `${paymentId}:${invoiceId}`;
}
